import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useCustomFilters from '../../hooks/useCustomFilters';
import { NetworkSchema } from '../../schema/network';
import Steps from './components/Steps';
import { onboardingDefault, validateSteps, mapOnboardingToFormPayload } from './helpers';
import { makeSelectIsLoading } from '../../redux/selectors/loading';
import { loadTalentOnboardingSubmit } from '../../redux/actions/talentOnboardingActions';
import { isTalentOnboardingComplete, isTalentOnboardingError } from '../../redux/selectors/talentNetworkSelector';
import useAllJobFunctions from '../../hooks/useAllJobFunctions';
import { useFilterSteps } from './hooks/useFilterSteps';
import { useTrackEvent } from './hooks/useTrackEvent';

export const TalentOnboarding = ({ network }) => {
  const [current, setCurrent] = useState(0);
  const { visibleCustomFilters } = useCustomFilters({ network });
  const onBoardingComplete = useSelector(isTalentOnboardingComplete);
  const error = useSelector(isTalentOnboardingError);
  const [onboarding, setOnBoarding] = useState(onboardingDefault);
  const { jobFunctions } = useAllJobFunctions();
  const isSaving = useSelector(makeSelectIsLoading('talentOnboardingSubmit'));
  const dispatch = useDispatch();
  const handleChange = useCallback((name, data) => setOnBoarding({ ...onboarding, [name]: data }), [onboarding]);

  const steps = useFilterSteps({
    customFilters: visibleCustomFilters,
    tnSettings: network?.tnSettings,
    jobFunctions,
    network,
  });

  useTrackEvent({
    current,
    onboarding,
    network,
    steps,
  });

  const isNextActive = useCallback(() => validateSteps(current, onboarding, steps), [current, onboarding, steps]);

  const handleNext = useCallback(() => {
    if (steps[current].id === 'profile' && isNextActive) {
      dispatch(
        loadTalentOnboardingSubmit({
          onboarding: mapOnboardingToFormPayload(onboarding),
          network,
        }),
      );

      return;
    }

    setCurrent(current + 1);
  }, [current, dispatch, isNextActive, network, onboarding, steps]);

  const handlePrevious = useCallback(() => {
    setCurrent(current - 1);
  }, [current]);

  useEffect(() => {
    if (onBoardingComplete && steps[current].id !== 'complete') {
      setCurrent(steps.length - 1);
    }
  }, [current, onBoardingComplete, steps]);

  return (
    <Steps
      current={current}
      handleNext={() => handleNext()}
      handlePrevious={handlePrevious}
      steps={steps}
      network={network}
      onChange={handleChange}
      isNextActive={isNextActive()}
      onboarding={onboarding}
      saving={isSaving}
      onBoardingComplete={onBoardingComplete}
      error={error}
    />
  );
};

TalentOnboarding.propTypes = {
  network: NetworkSchema.isRequired,
};

export default TalentOnboarding;
